import type { DensitySnapshot, RoundRankView, SideView, WrappedCoin } from "@screener/protocol";
import {
  formatUsd,
  groupThousands,
  sideLabel,
  tickerLabel,
  venueOf,
  wrappedMark,
} from "@/lib/labels";

/**
 * Показ величин таблицы §9. Ничего не вычисляет: пороги, состояния и флаги
 * приходят от демона готовыми, здесь они только называются по-человечески.
 *
 * Подписи, общие для нескольких экранов (рынок, сторона, триггер алерта,
 * тип ситуации, биржа, пара, состояние FSM, исход ленты, короткая запись
 * USD), живут в `@/lib/labels`, а не здесь: `alerts/alert-actions.ts`
 * нотификации нужны те же подписи, и своя копия в `density/format.ts`
 * разошлась бы с ними при первой же правке (stage6-acceptance-debts,
 * design.md решение 9). `formatUsd`, `tickerLabel`, `sideLabel`, `venueOf`
 * импортированы для `formatOptionalUsd`/`rowTitle` ниже, а не для повторного
 * экспорта — экраны, которым нужны сами подписи (включая `stateLabel`,
 * `outcomeLabel`), берут их из `@/lib/labels` напрямую.
 */

/** Объём в монетах: строка контракта, укороченная только по знакам после точки. */
export function formatQty(value: string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return value;
  }
  if (amount >= 1000) {
    return amount.toFixed(0);
  }
  return amount.toFixed(amount >= 1 ? 2 : 4);
}

/**
 * Цена якоря показывается как пришла: `Decimal` в число не превращается,
 * иначе цена потеряет точность на показе. Убирается только хвост нулей,
 * который биржа дописывает до своего шага.
 */
export function formatPrice(value: string): string {
  if (!value.includes(".")) {
    return value;
  }
  return value.replace(/0+$/, "").replace(/\.$/, "");
}

/**
 * Цена якоря в строке карточки §9 (1.45): меньше 0.1 — коротко, `(N)цифры`,
 * где N — нули сразу после точки, а цифры — остаток полной записи без
 * хвостовых нулей (`0.0000038427` → `(5)38427`). От 0.1 — как `formatPrice`.
 * Считается по строке контракта: перевод `Decimal` в число потерял бы знаки.
 */
export function formatCardPrice(value: string): string {
  const full = formatPrice(value);
  const match = /^0\.(0+)([1-9]\d*)$/.exec(full);
  if (match === null) {
    return full;
  }
  const [, zeros = "", digits = ""] = match;
  return `(${zeros.length})${digits}`;
}

export function formatDistance(distancePct: number | null): string {
  // Пустое значение читается как «своей стороны в стакане ещё нет» (§2),
  // и прочерк здесь честнее нуля: ноль означал бы касание.
  return distancePct === null ? "—" : `${distancePct.toFixed(2)}%`;
}

export function formatAge(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}с`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}м ${seconds % 60}с`;
  }
  const hours = Math.floor(minutes / 60);
  // С 24 часов — дни и часы: «41д 0ч» короче и читается быстрее «984ч 0м».
  if (hours >= 24) {
    return `${Math.floor(hours / 24)}д ${hours % 24}ч`;
  }
  return `${hours}ч ${minutes % 60}м`;
}

/**
 * Подписи коротки намеренно: это имя пометки, и читающий с экрана слышит
 * его на каждой круглой цене, по которой проходит стрелками. Полная фраза
 * про кратность стоит в подсказке строки, где её читают один раз.
 */
const ROUND_RANK_LABELS: Record<RoundRankView, string> = {
  whole: "круглая: порядок",
  half: "круглая: половина порядка",
  fine: "круглая: десятая доля",
};

/** Подпись ранга круглости цены §2 — та же карта-приём, что у остальных перечислений контракта. */
export function roundRankLabel(rank: RoundRankView): string {
  return ROUND_RANK_LABELS[rank];
}

/**
 * Процент величин §6.2. Знаки задаются вызывающим: дистанция и доля читаются
 * с сотыми, покрытие зон — целым.
 */
export function formatPercent(value: number, digits = 2): string {
  return `${value.toFixed(digits)}%`;
}

/** Прочерк для величины, которой в ответе нет вовсе — не то же самое, что ноль. */
const NOT_MEASURED = "—";

/** Величина §6.2, необязательная в контракте: `Option`, а не подсчитанный ноль. */
export function formatOptionalUsd(value: string | undefined): string {
  return value === undefined ? NOT_MEASURED : formatUsd(value);
}

/** Процент §6.2, который может быть не посчитан (`null` в контракте). */
export function formatOptionalPercent(value: number | null | undefined, digits = 2): string {
  return value === null || value === undefined ? NOT_MEASURED : formatPercent(value, digits);
}

/** Длительность §6.2 в секундах, если она вообще наступила (`null` иначе). */
export function formatOptionalAge(seconds: number | null | undefined): string {
  return seconds === null || seconds === undefined ? NOT_MEASURED : formatAge(seconds);
}

/**
 * Дистанция ближайшего фронтрана. При `frontrun_count === 0` фронтрана нет
 * вовсе, и `0.00%` читался бы как «стена вплотную» — самое тревожное из
 * возможных значений там, где путь к стене чист. Прочерк честнее нуля.
 */
export function formatFrontrunNearest(nearestPct: number, count: number): string {
  return count === 0 ? NOT_MEASURED : formatPercent(nearestPct);
}

/**
 * Момент по UTC, часами-минутами-секундами. Все таймстемпы системы — UTC,
 * и показываются такими же: местная зона на снимке, который сравнивают с
 * журналом демона, только путает.
 */
export function formatClock(tsMs: number): string {
  const moment = new Date(tsMs);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(moment.getUTCHours())}:${pad(moment.getUTCMinutes())}:${pad(moment.getUTCSeconds())} UTC`;
}

/** Момент по UTC, часы и минуты: «14:32 UTC» (плашка недоступных рынков, §132 п. 3). */
export function formatClockMinutes(tsMs: number): string {
  const moment = new Date(tsMs);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(moment.getUTCHours())}:${pad(moment.getUTCMinutes())} UTC`;
}

/**
 * Момент по UTC с датой: `ГГГГ-ММ-ДД ЧЧ:ММ:СС UTC`. Записи библиотеки одной
 * стены бывают из разных суток, и время без даты их не различает.
 */
export function formatDateTime(tsMs: number): string {
  const moment = new Date(tsMs);
  const pad = (value: number) => String(value).padStart(2, "0");
  const date = `${moment.getUTCFullYear()}-${pad(moment.getUTCMonth() + 1)}-${pad(moment.getUTCDate())}`;
  return `${date} ${formatClock(tsMs)}`;
}

/** Давность записи ленты исходов от общего «сейчас» таблицы. */
export function formatAgo(tsMs: number, nowMs: number): string {
  return formatAge(Math.max(0, Math.floor((nowMs - tsMs) / 1000)));
}

/**
 * Строка подсказки: полные величины, которые в колонках сокращены, плюс
 * единственное в интерфейсе место, где названы хоткеи строки. Названы все
 * четыре: подсказка, перечисляющая часть действий, оставляет остальные
 * невидимыми и врёт о наборе (review stage5-round-price, п. 11).
 *
 * Здесь же — полная фраза про круглость: сама пометка несёт только короткое
 * имя, чтобы не удлинять то, что читалка произносит на каждой строке.
 */
export function rowTitle(row: DensitySnapshot, wrapped: readonly WrappedCoin[]): string {
  const mark = wrappedMark(row.instrument.base, wrapped);
  const lines = [
    `${venueOf(row.instrument)} · ${tickerLabel(row.instrument, row.exchange_base)}${mark === undefined ? "" : ` · ${mark}`}`,
    `${sideLabel(row.side)} @ ${row.anchor_price}`,
    `${row.density_usd} USD · ${row.density_qty}`,
  ];
  if (row.round_rank) {
    lines.push(ROUND_RANK_TITLES[row.round_rank]);
  }
  lines.push("P — закрепить · N — заметка · C — скопировать тикер · F — избранное");
  return lines.join("\n");
}

/** Полные фразы §2 для подсказки строки — развёрнутые имена из `ROUND_RANK_LABELS`. */
const ROUND_RANK_TITLES: Record<RoundRankView, string> = {
  whole: "Круглая цена §2: кратна своему десятичному порядку",
  half: "Круглая цена §2: кратна половине порядка",
  fine: "Круглая цена §2: кратна десятой доле порядка",
};

/**
 * Сторона словом для подсказки и доступного имени строки карточки. Видимого
 * слова стороны в строке нет (ТЗ 1.45), цвет полосы — единственный видимый
 * знак; читалка получает сторону первым словом (design.md change
 * stage8-card-compact, решение 3). Не `sideLabel`: «BID»/«ASK» читалка
 * произносит по буквам.
 */
export const SIDE_WORDS: Record<SideView, string> = {
  ask: "Аск",
  bid: "Бид",
};

/**
 * Объём в USD для подсказки: целая часть десятичной строки с разделителями
 * разрядов (неразрывный пробел), без «$». Дробь отбрасывается без округления:
 * подсказка даёт порядок величины, точная сумма — в панели. Строка не идёт
 * через `Number`, поэтому большие суммы не теряют разряды.
 */
export function formatUsdGrouped(value: string): string {
  const match = /^(-?)(\d+)(?:\.\d*)?$/.exec(value);
  if (match === null) {
    return value;
  }
  const [, sign = "", intPart = "0"] = match;
  return `${sign}${groupThousands(intPart.replace(/^0+(?=\d)/, ""))}`;
}

/**
 * Подсказка строки карточки (§88, design.md stage9-redesign-cards, Д6):
 * первая строка называет сторону, полную цену и объём в USD, затем отметка
 * круглой цены, если есть, затем хоткеи. Короткая запись цены и краткий
 * объём на строке — лишь сжатие этой строки.
 */
export function cardRowTitle(row: DensitySnapshot): string {
  const lines: string[] = [
    `${SIDE_WORDS[row.side]} · ${formatPrice(row.anchor_price)} · ${formatUsdGrouped(row.density_usd)} USD`,
  ];
  if (row.round_rank) {
    lines.push(ROUND_RANK_TITLES[row.round_rank]);
  }
  lines.push("P — закрепить · N — заметка · C — скопировать тикер · F — избранное");
  return lines.join("\n");
}
